import React, { useState, useEffect } from "react";
import Header from "../../../../Componets/Header";
import Loadercomponent from "../../../../Componets/Loader";
import { Card, Table, Typography, Empty } from "antd";
import { TableOutlined } from "@ant-design/icons";
import trialBalanceService from "../../../../Controllers/Accounting/Services/trialBalanceService";
import dayjs from "dayjs";
import headers from "../../../../Data/Tables/Accounting/ReportEntry/Trailbalance/headers";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { Button } from "antd";
import ScrollWrapper from "../../../../Componets/ScrollWrapper";

const { Text } = Typography;

const safeToLocaleString = (value) => {
  const num = Number(value) || 0;
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
};

const TrialBalanceReport = () => {
  const [loader, setLoader] = useState(true);
  const [header, setHeader] = useState(headers);
  const [data, setData] = useState([]);
  const [dates, setDates] = useState({});
  const [totals, setTotals] = useState({ totalDebit: 0, totalCredit: 0 });

  const formdata = (Data) => {
    setDates(Data.date);
  };

  const loadData = async () => {
    setLoader(true);
    try {
      const requestData =
        dates.start && dates.end
          ? { startDate: dates.start, endDate: dates.end }
          : {};

      const result = await trialBalanceService.getTrialBalance(requestData);

      if (result.success) {
        setData(result.accounts || []);
        setTotals({
          totalDebit: result.totalDebit || 0,
          totalCredit: result.totalCredit || 0,
        });
      }
    } catch (err) {
      console.error("Error loading Trial Balance:", err);
    } finally {
      setLoader(false);
    }
  };

  const headerReset = () => {
    let componentData = [];
    headers?.components?.map((value, index) => {
      componentData?.push({
        ...value,
        value: null,
      });
    });
    setHeader({ ...headers, components: componentData });
  };

  useEffect(() => {
    loadData();
    headerReset();
  }, []);

  useEffect(() => {
    loadData();
  }, [dates]);

  const columns = [
    {
      title: "Account Name",
      dataIndex: "name",
    },
    {
      title: "Account Type",
      dataIndex: "accountType",
    },
    {
      title: "Debit",
      dataIndex: "debit",
      align: "right",
      render: (value) => (
        <Text type="success">{safeToLocaleString(value)}</Text>
      ),
    },
    {
      title: "Credit",
      dataIndex: "credit",
      align: "right",
      render: (value) => <Text type="danger">{safeToLocaleString(value)}</Text>,
    },
  ];

  const downloadPDF = () => {
    if (!data.length) return;

    const pdf = new jsPDF("p", "mm", "a4");

    const branch = data[0]?.branch || {};
    const generatedDate = dayjs().format("MMM D, YYYY");

    pdf.setFontSize(18);
    pdf.text(branch.name || "Company Name", 105, 20, { align: "center" });

    pdf.setFontSize(11);
    const branchLine = `${branch.email || "-"} | ${branch.phone || "-"} | ${
      branch.country || "-"
    }`;
    pdf.text(branchLine, 105, 28, { align: "center" });

    pdf.setFontSize(14);
    pdf.text("Trial Balance Report", 105, 38, { align: "center" });

    if (dates.start && dates.end) {
      pdf.setFontSize(11);
      pdf.text(
        `Period: ${dayjs(dates.start).format("MMM D, YYYY")} - ${dayjs(
          dates.end,
        ).format("MMM D, YYYY")}`,
        105,
        44,
        { align: "center" },
      );
    }

    autoTable(pdf, {
      startY: 50,
      head: [["Account Name", "Account Type", "Debit", "Credit"]],
      body: data.map((item) => [
        item.name,
        item.accountType,
        safeToLocaleString(item.debit),
        safeToLocaleString(item.credit),
      ]),
      theme: "grid",
      styles: { fontSize: 10 },
      headStyles: { fillColor: [24, 144, 255] },
    });

    const finalY = pdf.lastAutoTable.finalY + 10;
    pdf.setFontSize(12);
    pdf.text(
      `Total Debit: ${safeToLocaleString(totals.totalDebit)}`,
      14,
      finalY,
    );
    pdf.text(
      `Total Credit: ${safeToLocaleString(totals.totalCredit)}`,
      100,
      finalY,
    );

    pdf.save(`TrialBalanceReport_${generatedDate}.pdf`);
  };

  const downloadExcel = () => {
    if (!data.length) return;

    const rows = [
      ["Trial Balance Report"],
      [
        dates.start && dates.end
          ? `Period: ${dayjs(dates.start).format("MMM D, YYYY")} - ${dayjs(
              dates.end,
            ).format("MMM D, YYYY")}`
          : "",
      ],
      [],
      ["Account Name", "Account Type", "Debit", "Credit"],
    ];

    data.forEach((item) => {
      rows.push([
        item.name,
        item.accountType,
        safeToLocaleString(item.debit),
        safeToLocaleString(item.credit),
      ]);
    });

    rows.push([]);
    rows.push([
      "TOTAL",
      "",
      safeToLocaleString(totals.totalDebit),
      safeToLocaleString(totals.totalCredit),
    ]);

    const content = rows.map((row) => row.join("\t")).join("\n");

    const blob = new Blob([content], {
      type: "application/vnd.ms-excel",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `TrialBalanceReport_${dayjs().format("YYYY-MM-DD")}.xls`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Header headers={header} setHeaders={setHeader} formdata={formdata}>
      <div className="flex gap-3 justify-end mt-4">
        <Button
          onClick={downloadExcel}
          className="text-medium form-save-button"
          type="primary"
        >
          Export as Excel
        </Button>

        <Button
          onClick={downloadPDF}
          className="text-medium form-save-button"
          type="primary"
        >
          Export as PDF
        </Button>
      </div>
      <Loadercomponent loader={loader}>
        <ScrollWrapper>
          <div>
            <Card
              title={
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <TableOutlined />
                  <span>Trial Balance Report</span>
                </div>
              }
            >
              {!loader && data.length > 0 ? (
                <>
                  <div style={{ marginBottom: 16, textAlign: "center" }}>
                    {dates.start && dates.end
                      ? `Period: ${dayjs(dates.start).format(
                          "MMM D, YYYY",
                        )} - ${dayjs(dates.end).format("MMM D, YYYY")}`
                      : ""}
                  </div>

                  <Table
                    columns={columns}
                    dataSource={data.map((item, index) => ({
                      key: index,
                      ...item,
                    }))}
                    pagination={false}
                    size="small"
                    style={{ marginBottom: 24 }}
                  />

                  <div
                    style={{
                      display: "flex",
                      justifyContent: "flex-end",
                      gap: 32,
                    }}
                  >
                    <div>
                      <Text strong>Total Debit:</Text>
                      <br />
                      <Text type="success">
                        {safeToLocaleString(totals.totalDebit)}
                      </Text>
                    </div>
                    <div>
                      <Text strong>Total Credit:</Text>
                      <br />
                      <Text type="danger">
                        {safeToLocaleString(totals.totalCredit)}
                      </Text>
                    </div>
                  </div>
                </>
              ) : (
                <Empty
                  description={
                    loader ? "Loading report..." : "No data available"
                  }
                />
              )}
            </Card>
          </div>
        </ScrollWrapper>
      </Loadercomponent>
    </Header>
  );
};

export default TrialBalanceReport;
